import React, { FormEvent } from "react"
import * as Yup from 'yup';
export class ValidationResult<TErrors>
{
    constructor(type: { new(): TErrors }) {
        this.errors = new type();
    }
    isValid: boolean = true;
    errors: TErrors;
}

type IErrors<FormData> = {
    [P in keyof FormData]?: string;
};
type ITouched<FormData> = {
    [P in keyof FormData]?: boolean;
};

interface IFormState<T> {
    errors: IErrors<T>;
    item: T;
    originalItem: T;
    touched: ITouched<T>;
}

export interface IFormProps<T> {
    initialValues: T;
    onSubmit: (item: T) => void;
    validationSchema?: Yup.ObjectSchema<Partial<T>> | ((data: T) => ValidationResult<IErrors<T>>);
    noPrompt?: boolean;
    isEqual?: (i1: T | null | undefined, i2: T | null | undefined) => boolean;
}

type SetHandler<T> = {
    set<T1 extends keyof T>(field: T1, value: T[T1]): void;
    set<T1 extends keyof T, T2 extends keyof NonNullable<T[T1]>>(field: T1, field2: T2, value: NonNullable<T[T1]>[T2]): void;
    set<T1 extends keyof T, T2 extends keyof T[T1], T3 extends keyof T[T1][T2]>(field: T1, field2: T2, field3: T3, value: T[T1][T2][T3]): void;
}

export interface IContextProps<T> extends SetHandler<T> {
    values: T;
    onError?: (errors: IErrors<T>) => void
    errors: IErrors<T>;
    handleSubmit: React.FormEventHandler<HTMLFormElement>;
    dirty: boolean;
}

export const isEqual = <T,>(i1: T | null | undefined, i2: T | null | undefined, k?: string) => {
    if (typeof i1 == "object") {
        if (i1 == i2)
            return true;
        if (i1 == null)
            return false;
        if (i2 == null)
            return false;
        if (i1 instanceof Date) {
            return i1.valueOf() == (i2 as any).valueOf()
        }
        else if (Array.isArray(i1) && Array.isArray(i2)) {
            if (i1.length != i2.length)
                return false;
            return i1.find((x, i) => !isEqual(x, i2[i])) == null;
        }
        if (typeof i1 === "number" && typeof i2 === "number")
            return Math.abs(i1 - i2.valueOf()) < 0.00001;
        return Object.keys(i1).every(k => isEqual(i1[k], i2[k], k));
    }
    else {
        if (!i1 && !i2)
            return true;
        if (i1 != i2) {
            if (process.env.NODE_ENV !== "production")
                console.log(i1, i2, k)
            return false;
        }
        return true;
    }
}


const Context = React.createContext<IContextProps<any>>({ values: null, errors: {}, handleSubmit: () => { }, set: () => { }, dirty: false });

export type OpenFormsChildrenType<T> = ((openForms: IContextProps<T>) => React.ReactNode) | React.ReactElement;
type OpenFormsContextProps<T> = IFormProps<T> & { children?: OpenFormsChildrenType<T> };

export class OpenFormsContext<T> extends React.Component<OpenFormsContextProps<T>, IFormState<T>>{


    constructor(props: OpenFormsContextProps<T>) {
        super(props);
        this.state = { errors: {}, originalItem: props.initialValues, item: props.initialValues, touched: {} }
    }
    private onError = (errors: IErrors<unknown>) => {
        this.setState({ errors })
    }
    private handleSubmit = async (e: FormEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const { validationSchema } = this.props;
        const { item } = this.state;
        if (typeof validationSchema == "function") {
            const result = validationSchema(item);
            if (result.isValid) {
                this.setState({ originalItem: item }, () => this.props.onSubmit(item));
            }
            else {
                const errors = {} as IErrors<T>;
                for (const i in result.errors)
                    errors[i] = result.errors[i];
                this.setState({
                    errors
                })
            }
        }
        else {
            try {
                await validationSchema?.validate(item, { abortEarly: false })
                this.setState({ originalItem: item }, () => this.props.onSubmit(item));
            }
            catch (e) {
                if (Yup.ValidationError.isError(e)) {
                    if (e.path) {
                        this.setState({
                            errors: {
                                ...this.state.errors,
                                [e.path]: e.message,
                            },
                        });
                    }
                    else {
                        const errors = {} as any;
                        for (const i of e.inner)
                            errors[i.path] = i.message;
                        this.setState({
                            errors
                        })
                    }
                }
            }
        }
    }

    componentDidUpdate(oldProps: OpenFormsContextProps<T>) {
        if (!(this.props.isEqual ?? isEqual)(oldProps.initialValues, this.props.initialValues)) {
            this.setState({ originalItem: this.props.initialValues });
        }
    }

    private getError = async (path: keyof T, item: T) => {
        const { validationSchema } = this.props;
        if (typeof validationSchema == "function") {
            return validationSchema(item).errors[path] ?? "";
        }
        else {
            try {
                await validationSchema?.validateAt(path as any, item)
                return "";
            }
            catch (e) {
                if (Yup.ValidationError.isError(e)) {
                    if (e.name === "ValidationError")
                        return e.message;
                }
                return "";
            }
        }
    }

    private getValidationErrors = async (path: keyof T, item: T) => {
        const errors = { ...this.state.errors };
        let skipPath = false;
        for (const key in this.state.touched) {
            if (this.state.touched[key] == true) {
                errors[key] = await this.getError(key, item)
                if (key == path)
                    skipPath = true
            }
        }
        if (!skipPath)
            errors[path] = await this.getError(path, item)
        return errors;
    }
    private setValidationErrors = async (p1: keyof T, newItem: T) => {
        const errors = await this.getValidationErrors(p1, newItem);
        if (!isEqual(errors, this.state.errors))
            this.setState({ errors, });
    }

    private set: SetHandler<T>["set"] = (async (p1: keyof T, p2: any, p3: any | null, p4: any | null) => {
        const { item } = this.state;
        const newItem = { ...item };
        const touched = { ...this.state.touched };
        if (p4 !== undefined) {
            newItem[p1] = {
                ...newItem[p1],
                [p2]: { ...(newItem as any)[p1][p2], [p3]: p4, },
            }
            touched[p1] = true;
        }
        else if (p3 !== undefined) {
            newItem[p1] = { ...newItem[p1], [p2]: p3, }
            touched[p1] = true;
        }
        else {
            newItem[p1] = p2;
            touched[p1] = true;
        }
        this.setState({
            item: newItem,
            touched,
        }, () => this.setValidationErrors(p1, newItem))
    }) as any;

    render() {
        const { noPrompt, children, ...props } = this.props;
        const dirty = !(this.props.isEqual ?? isEqual)(this.state.originalItem, this.state.item);
        const context = {
            ...props,
            onError: this.onError,
            errors: this.state.errors,
            set: this.set,
            handleSubmit: this.handleSubmit,
            dirty,
            values: this.state.item,
        };
        return <Context.Provider value={context}>
            {typeof children == "function"
                ? children(context)
                : children
            }
        </Context.Provider>
    }
}

export interface IFormItemProps<T> {
    children: (props: IContextProps<T>) => React.ReactElement | null;
}
export class FormItem<T> extends React.PureComponent<IFormItemProps<T>> {
    render() {
        const Child = this.props.children;
        return <Context.Consumer>
            {(context: IContextProps<T>) => {
                return <Child {...context} />
            }}
        </Context.Consumer>;
    }
}
